import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';

import { GameBase } from '../../games/game-base';
import { GameFactory } from '../../games/game-factory.service';
import { CardState } from '../../games/models/game-view.model';
import { AppPhase, DealingSubPhase, GameType, RouletteSubPhase } from '../../models/communication/app-enums.model';
import { GameResultChangeEvent } from '../../models/communication/app-events.model';
import { InitialTableState, TableUpdate } from '../../models/communication/app-state.model';
import { ResultChange } from '../../services/result-change.service';
import { GameSwitchComponent } from '../game-switch/game-switch.component';

/**
 * Generates the game table components based on the app state.
 * Initializes the game classes using the `GameFactory`.
 * Responsible for game instances' and relevant components' life cycles.
 *
 * It passes the relevant changes calling the `onChange` method.
 * Projects the `CountDownComponent` and `ResultChangeComponent`.
 */
@Component({
    selector: 'app-game-factory',
    imports: [CommonModule, GameSwitchComponent],
    templateUrl: './game-factory.component.html',
    styleUrl: './game-factory.component.scss'
})
export class GameFactoryComponent implements OnChanges {
    @Input()
    gameType!: GameType;

    @Input()
    initialData?: InitialTableState;

    @Input()
    tableUpdate?: TableUpdate;

    @Input()
    gameResultChange?: GameResultChangeEvent;

    @Input()
    appPhase?: AppPhase;

    @Input()
    dealingSubPhase?: DealingSubPhase | RouletteSubPhase;

    protected readonly AppPhase = AppPhase;
    protected readonly GameType = GameType;

    protected game?: GameBase;

    constructor(
        private readonly gameFactory: GameFactory,
        protected readonly resultChange: ResultChange
    ) {}

    ngOnChanges(changes: Partial<SimpleChanges>): void {
        // If the game is not instantiated before RoundEnd happens
        // return early and show the message (in the template).
        if (changes.appPhase && this.appPhase === AppPhase.RoundEnd && !this.game) {
            return;
        }

        if (changes.gameType || changes.initialData || (changes.appPhase && this.appPhase === AppPhase.NewRoundPreparation)) {
            this.initGame();
        }

        if (this.game) {
            if (this.appPhase === AppPhase.RoundEnd) {
                return;
            }

            if (
                (changes.appPhase && this.appPhase !== undefined) ||
                (changes.dealingSubPhase && this.dealingSubPhase !== undefined) ||
                (changes.tableUpdate && this.tableUpdate !== undefined)
            ) {
                this.updateGame();
            }

            if (changes.gameResultChange && this.gameResultChange) {
                // Only update the hands values.
                // We don't need to update the cards as they are
                // already changed by the ResultChange service.
                this.game.updateHandsValues(this.gameResultChange.handValue);

                if (this.gameResultChange.outcome !== undefined) {
                    const { outcome, outcomes } = this.gameResultChange;
                    this.game.handleOutcome({ outcome, outcomes });

                    // If there are cards on the table with the state other than SHOW
                    // (like message face Up/Down) hide them.
                    for (const card of this.game.getCards()) {
                        if (this.game.getCardState(card.position) !== CardState.Show) {
                            this.game.hideCard(card.position);
                        }
                    }
                }
            }
        }
    }

    private initGame(): void {
        this.game = this.gameFactory.create(this.gameType, this.initialData);
        this.resultChange.setGame(this.game);
    }

    private updateGame(): void {
        this.game?.flow.update(this.tableUpdate, this.appPhase, this.dealingSubPhase);
    }
}
